/**
 * Decides what the client should do when MoveMatchingDoneEvent fires.
 *
 * Lifted out of the results store so it can be unit-tested without mocking
 * Pinia / vue-router / Pusher. Keeps the store a thin wiring layer.
 *
 * The "stash" case is what fixes CU-86d2jf6c5: matching can finish on the
 * backend while the customer is still entering an OTP. We don't navigate away
 * in that case — the results store parks the intent and
 * session.verifyPhoneCode drains it after phone_verified flips.
 */

export interface LeadDistributionShape {
    already_distributed?: boolean;
    delivered_company_ids?: number[];
    can_opt_in?: boolean;
}

export type MatchingDoneDecision =
    | { kind: 'ignore' }
    | { kind: 'navigate'; recommended: boolean }
    | { kind: 'stash'; recommended: boolean };

export interface MatchingDoneInput {
    routePath: string;
    hasRecommendedMatches: boolean;
    isLocal: boolean;
    isInterstate: boolean;
    leadDistribution: LeadDistributionShape | null | undefined;
    requiresPhoneVerification: boolean;
    phoneVerified: boolean;
}

export function decideMatchingDoneNavigation(input: MatchingDoneInput): MatchingDoneDecision {
    if (input.routePath === '/results/review') {
        return { kind: 'ignore' };
    }

    const ld = input.leadDistribution;
    const delivered = ld?.delivered_company_ids ?? [];
    const hasLeadDistributionDeliveries =
        Boolean(ld?.already_distributed) || (Array.isArray(delivered) && delivered.length > 0);
    const canShowLeadDistributionTile = input.isLocal && ld?.can_opt_in === true && !hasLeadDistributionDeliveries;

    // Interstate orders always route to /results/recommended when matching
    // completes — keeps the interstate UX consistent with the organic-pricing-off
    // flow shipped in PR #378.
    const recommended = Boolean(input.hasRecommendedMatches || canShowLeadDistributionTile || input.isInterstate);

    if (input.requiresPhoneVerification && !input.phoneVerified) {
        return { kind: 'stash', recommended };
    }

    return { kind: 'navigate', recommended };
}
